import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import arrayMove from 'array-move';
import { CKButton } from 'clearkit';
import { sortableContainer, sortableElement } from 'react-sortable-hoc';
import { AutoSizer, List } from 'react-virtualized';

import LoadingArea from '~/components/LoadingArea';
import { formatColumnLabel } from '~/lib/helpers';

import SearchTextField from '../SearchTextField';
import Table, { TableBody, TableCell, TableRow } from '../Table';
import styles from './ColumnSelectorStyles.style';

const SortableChip = sortableElement(
  ({ className, value, toggleSelection, index }) => (
    <li key={index} index={index} style={{ zIndex: 99999999 }}>
      <Chip
        className={className}
        label={formatColumnLabel(value)}
        onDelete={() => toggleSelection(value)}
      />
    </li>
  ),
);

const SortableContainer = sortableContainer(({ children, classes }) => (
  <ul className={classes.selectedColumnList}>{children}</ul>
));

class ColumnSelectorDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      columns: props.columns || [],
      allColumns: props.allColumns || [],
      filter: null,
    };
  }

  toggleSelection = (columnKey) => {
    const { columns } = this.state;
    const activeIndex = columns.indexOf(columnKey);

    if (activeIndex === -1) {
      this.setState((prevState) => ({
        columns: [...prevState.columns, columnKey],
      }));
    } else {
      this.setState((prevState) => ({
        columns: [
          ...prevState.columns.slice(0, activeIndex),
          ...prevState.columns.slice(activeIndex + 1),
        ],
      }));
    }
  };

  onClose = (updateColumns) => {
    const { onClose } = this.props;
    const { columns } = this.state;
    onClose(updateColumns && columns);
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(({ columns }) => ({
      columns: arrayMove(columns, oldIndex, newIndex),
    }));
  };

  isMatch = (left, right) =>
    left.toLowerCase().indexOf(right.toLowerCase()) !== -1;

  getFilteredColumns = () => {
    const { allColumns, filter } = this.state;

    if (!filter) return allColumns;

    return allColumns.filter((col) => this.isMatch(col, filter));
  };

  renderColumn = (columnKey) => {
    const { classes } = this.props;

    const checked = this.state.columns.includes(columnKey);

    return (
      <TableRow
        className={classes.tableRow}
        selected={checked}
        onClick={() => this.toggleSelection(columnKey)}
        hover>
        <TableCell className={classes.checkCell}>
          <Checkbox
            color="primary"
            key={columnKey}
            checked={checked}
            value={columnKey}
          />
        </TableCell>

        <TableCell className={classes.checkboxLabel}>
          {formatColumnLabel(columnKey)}
        </TableCell>
      </TableRow>
    );
  };

  renderRow = ({ index, key, style }) => {
    const filteredColumns = this.getFilteredColumns();

    const column = filteredColumns[index];

    return (
      <div key={key} style={style} index={index}>
        {column && this.renderColumn(column)}
      </div>
    );
  };

  renderList = () => {
    const { columns, filter } = this.state;

    const filteredColumns = this.getFilteredColumns();

    return (
      <AutoSizer defaultHeight={500}>
        {({ height, width }) => (
          <List
            width={width}
            height={height}
            rowCount={filteredColumns.length}
            rowHeight={55}
            rowRenderer={this.renderRow}
            columns={columns}
            filter={filter}
          />
        )}
      </AutoSizer>
    );
  };

  render() {
    const { open, classes } = this.props;
    const { allColumns, columns } = this.state;

    return (
      <Dialog
        maxWidth={false}
        open={open}
        onExited={() => this.setState({ filter: null })}>
        <div className={classes.dialogContainer}>
          <div>
            <DialogTitle disableTypography>
              Choose columns to display
            </DialogTitle>

            <div className={classes.root}>
              <div className={classes.header}>
                <SearchTextField
                  onSearch={(e) => this.setState({ filter: e.target.value })}
                  placeholder="Filter columns"
                  fullWidth
                />
              </div>

              <Table className={classes.tableRoot}>
                <TableBody>
                  <LoadingArea loading={!allColumns.length}>
                    {this.renderList()}
                  </LoadingArea>
                </TableBody>
              </Table>
            </div>
          </div>

          <div className={classes.selectedColumnContainer}>
            <DialogTitle disableTypography>
              Selected columns ({columns.length})
            </DialogTitle>

            <DialogContent>Drag columns to re-order</DialogContent>

            {columns.length > 0 ? (
              <SortableContainer
                classes={classes}
                distance={1}
                helperClass={classes.drag}
                onSortEnd={this.onSortEnd}>
                {columns.map((columnKey, index) => (
                  <SortableChip
                    className={classes.selectedColumn}
                    value={columnKey}
                    key={index}
                    toggleSelection={this.toggleSelection}
                    index={index}
                  />
                ))}
              </SortableContainer>
            ) : (
              <div className={classes.selectedColumnList}>
                <Typography>No columns selected</Typography>
              </div>
            )}
            <DialogActions className={classes.actionRow}>
              <CKButton variant="secondary" onClick={() => this.onClose(false)}>
                Cancel
              </CKButton>

              <CKButton
                isDisabled={!columns.length}
                onClick={() => this.onClose(true)}
                variant="bold"
                variantColor="blue">
                Done
              </CKButton>
            </DialogActions>
          </div>
        </div>
      </Dialog>
    );
  }
}

ColumnSelectorDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  classes: PropTypes.shape({ root: PropTypes.string.isRequired }).isRequired,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  allColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default withStyles(styles)(ColumnSelectorDialog);
